import { convertFormGiToBillingRequirement, isStorageBillingUnit, normalizeQuantityUnit } from '../components/quantityUnit';
import { type VendorDeviceSelection, hasVendorDeviceSelection } from '../components/vendorAndDeviceRadioList';

type VendorMetaLike = VendorDeviceSelection | Record<string, unknown> | null | undefined;

/** pricing：算价；order：下单/提交资源组装 */
export type ResourceQuantityPurpose = 'pricing' | 'order';

export function isPostPaidPaymentTiming(paymentTiming?: string): boolean {
  return paymentTiming === 'POST_PAID';
}

export function hasPricedVendorSelection(meta?: VendorMetaLike): boolean {
  return hasVendorDeviceSelection(meta as VendorDeviceSelection | null | undefined);
}

function readQuantityText(quantity: unknown): string | undefined {
  if (quantity === undefined || quantity === null) return undefined;
  const text = String(quantity).trim();
  return text || undefined;
}

function readVendorQuantityUnit(meta?: VendorMetaLike): string | undefined {
  if (!meta || typeof meta !== 'object') return undefined;
  return normalizeQuantityUnit((meta as { quantityUnit?: unknown }).quantityUnit);
}

/**
 * 表单存储类数量为 Gi；计费项单位不同时换算。
 * 后付费询单价固定「1」表示 1 个计费单位，不做换算。
 */
function maybeConvertStorageGiRequirement(
  requirement: string | undefined,
  options: {
    paymentTiming?: string;
    vendorMeta?: VendorMetaLike;
    purpose?: ResourceQuantityPurpose;
    quantityAlreadyInBillingUnit?: boolean;
  },
): string | undefined {
  if (requirement == null || options.quantityAlreadyInBillingUnit) return requirement;

  const purpose = options.purpose ?? 'pricing';
  if (isPostPaidPaymentTiming(options.paymentTiming) && purpose === 'pricing' && requirement === '1') {
    return requirement;
  }

  const billingUnit = readVendorQuantityUnit(options.vendorMeta);
  if (!isStorageBillingUnit(billingUnit) || billingUnit === 'Gi') return requirement;
  return convertFormGiToBillingRequirement(requirement, billingUnit) ?? requirement;
}

export interface VendorQuantityOptionalOptions {
  billModelId?: string;
  paymentTiming?: string;
  /** SKU 类型；custom + sku-create 时数量非必填 */
  skuType?: string;
  /** 资源编辑模式；仅 sku-create 放宽数量，下单仍按原规则 */
  resourceEditMode?: string;
}

/** 计价 UI：已选厂商设备时可不填数量（后付费，或先付费自定义规格创建）；字段仍展示 */
export function isVendorQuantityOptional(options: VendorQuantityOptionalOptions): boolean {
  if (!options.billModelId) return false;
  if (isPostPaidPaymentTiming(options.paymentTiming)) return true;
  return options.skuType === 'custom' && options.resourceEditMode === 'sku-create';
}

/**
 * @deprecated 数量/容量始终展示，仅用 isVendorQuantityOptional 控制是否必填；恒为 false
 */
export function shouldHideVendorQuantityFields(_options: VendorQuantityOptionalOptions): boolean {
  return false;
}

/**
 * @deprecated 请优先使用 isVendorQuantityOptional；保留兼容旧调用签名
 */
export function isPostPaidVendorQuantityOptional(
  paymentTiming?: string,
  billModelId?: string,
  extra?: Pick<VendorQuantityOptionalOptions, 'skuType' | 'resourceEditMode'>,
): boolean {
  return isVendorQuantityOptional({ billModelId, paymentTiming, ...extra });
}

/**
 * 解析资源 requirement。
 * - 算价 pricing：后付费且已选厂商 → 固定 1（询单价）；先付费（固定/自定义）→ 有数量才返回，否则不组装
 * - 组装 order：后付费且已选厂商 → 填写值，未填为 0；先付费 → 有数量才返回，否则不组装
 * - 未选厂商：仅返回已填写数量
 * - 存储类：表单数量为 Gi，按选型 quantityUnit（如 Mi）换算后再返回
 */
export function resolvePricingRequirement(
  quantity: unknown,
  options: {
    paymentTiming?: string;
    vendorMeta?: VendorMetaLike;
    purpose?: ResourceQuantityPurpose;
    /** @deprecated 先付费固定/自定义规则已统一，保留入参兼容旧调用 */
    skuType?: string;
    /** 数量已是计费单位时跳过 Gi→计费换算 */
    quantityAlreadyInBillingUnit?: boolean;
  },
): string | undefined {
  const purpose = options.purpose ?? 'pricing';
  const text = readQuantityText(quantity);
  const hasVendor = hasPricedVendorSelection(options.vendorMeta);

  let result: string | undefined;
  if (isPostPaidPaymentTiming(options.paymentTiming) && hasVendor) {
    if (purpose === 'order') {
      result = text ?? '0';
    } else {
      result = '1';
    }
  } else {
    result = text;
  }

  return maybeConvertStorageGiRequirement(result, { ...options, purpose });
}
